import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styles from './Settings.module.css';

const Settings = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'general');
  
  // Update active tab when location state changes
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  const [notifications, setNotifications] = useState({
    emailUpdates: true,
    projectAlerts: true,
    bidNotifications: true,
    inventoryAlerts: false,
    teamUpdates: true,
    securityAlerts: true
  });

  const [integrations, setIntegrations] = useState({
    googleCalendar: true,
    microsoftTeams: false,
    slack: true,
    dropbox: false,
    quickbooks: true
  });

  const [projectDefaults, setProjectDefaults] = useState({
    timeZone: 'UTC-5',
    currency: 'USD',
    measurementUnit: 'imperial',
    workweekDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    autoBackup: true,
    dataRetention: '90'
  });

  const handleNotificationChange = (key) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleIntegrationToggle = (key) => {
    setIntegrations(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleProjectDefaultChange = (key, value) => {
    setProjectDefaults(prev => ({
      ...prev,
      [key]: value
    }));
  };

  return (
    <div className={styles.settingsWrapper}>
      <div className={styles.settingsHeader}>
        <div className={styles.headerContent}>
          <h1>Settings</h1>
          <p>Manage your application preferences and configurations</p>
        </div>
      </div>

      <div className={styles.settingsContainer}>
        <div className={styles.sidebar}>
          <button 
            className={`${styles.tabButton} ${activeTab === 'general' ? styles.active : ''}`}
            onClick={() => setActiveTab('general')}
          >
            <i className="fas fa-cog"></i>
            General
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'notifications' ? styles.active : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            <i className="fas fa-bell"></i>
            Notifications
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'integrations' ? styles.active : ''}`}
            onClick={() => setActiveTab('integrations')}
          >
            <i className="fas fa-plug"></i>
            Integrations
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'project-defaults' ? styles.active : ''}`}
            onClick={() => setActiveTab('project-defaults')}
          >
            <i className="fas fa-project-diagram"></i>
            Project Defaults
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'team' ? styles.active : ''}`}
            onClick={() => setActiveTab('team')}
          >
            <i className="fas fa-users"></i>
            Team Management
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'api' ? styles.active : ''}`}
            onClick={() => setActiveTab('api')}
          >
            <i className="fas fa-code"></i>
            API Settings
          </button>
        </div>

        <div className={styles.mainContent}>
          {activeTab === 'notifications' && (
            <div className={styles.section}>
              <h2>Notification Preferences</h2>
              <div className={styles.settingsList}>
                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <h3>Email Updates</h3>
                    <p>Receive daily digest of project updates</p>
                  </div>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox"
                      checked={notifications.emailUpdates}
                      onChange={() => handleNotificationChange('emailUpdates')}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
                {/* Add more notification settings */}
              </div>
            </div>
          )}

          {activeTab === 'integrations' && (
            <div className={styles.section}>
              <h2>Connected Services</h2>
              <div className={styles.integrationsList}>
                <div className={styles.integrationItem}>
                  <div className={styles.integrationInfo}>
                    <img src="/google-calendar.png" alt="Google Calendar" />
                    <div>
                      <h3>Google Calendar</h3>
                      <p>Sync project deadlines and meetings</p>
                    </div>
                  </div>
                  <button 
                    className={`${styles.integrationButton} ${integrations.googleCalendar ? styles.connected : ''}`}
                    onClick={() => handleIntegrationToggle('googleCalendar')}
                  >
                    {integrations.googleCalendar ? 'Connected' : 'Connect'}
                  </button>
                </div>
                {/* Add more integration options */}
              </div>
            </div>
          )}

          {activeTab === 'project-defaults' && (
            <div className={styles.section}>
              <h2>Project Default Settings</h2>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Time Zone</label>
                  <select 
                    value={projectDefaults.timeZone}
                    onChange={(e) => handleProjectDefaultChange('timeZone', e.target.value)}
                  >
                    <option value="UTC-5">Eastern Time (UTC-5)</option>
                    <option value="UTC-6">Central Time (UTC-6)</option>
                    <option value="UTC-7">Mountain Time (UTC-7)</option>
                    <option value="UTC-8">Pacific Time (UTC-8)</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>Currency</label>
                  <select 
                    value={projectDefaults.currency}
                    onChange={(e) => handleProjectDefaultChange('currency', e.target.value)}
                  >
                    <option value="USD">US Dollar ($)</option>
                    <option value="EUR">Euro (€)</option>
                    <option value="GBP">British Pound (£)</option>
                  </select>
                </div>
                {/* Add more project default settings */}
              </div>
            </div>
          )}

          {activeTab === 'api' && (
            <div className={styles.section}>
              <h2>API Configuration</h2>
              <div className={styles.apiSection}>
                <div className={styles.apiKey}>
                  <h3>API Key</h3>
                  <div className={styles.apiKeyDisplay}>
                    <input 
                      type="text" 
                      value="sk_live_xxxxxxxxxxxxxxxxxxxxx" 
                      readOnly 
                    />
                    <button className={styles.copyButton}>
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                  <button className={styles.generateButton}>
                    <i className="fas fa-sync-alt"></i>
                    Generate New Key
                  </button>
                </div>
                <div className={styles.apiLimits}>
                  <h3>Rate Limits</h3>
                  <div className={styles.limitItem}>
                    <span>Requests per minute</span>
                    <strong>60</strong>
                  </div>
                  <div className={styles.limitItem}>
                    <span>Requests per hour</span>
                    <strong>3600</strong>
                  </div>
                </div>
              </div>
            </div>
          )}


        </div>
      </div>
    </div>
  );
};

export default Settings; 